import { useEffect, useMemo, useRef } from 'react';
import { pollOnce } from './pollService';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const DEFAULT_POLL_MS = 10_000;
const MIN_POLL_MS = 2_000;
const MAX_POLL_MS = 60_000;

export const useServerPolling = ({
    enabled,
    handlers,
    initialPollMs = DEFAULT_POLL_MS,
} = {}) => {
    const seenEventIdsRef = useRef(new Set());
    const timeoutRef = useRef(null);
    const stoppedRef = useRef(false);
    const inFlightRef = useRef(false);
    const backoffRef = useRef({
        baseMs: clamp(initialPollMs, MIN_POLL_MS, MAX_POLL_MS),
        currentMs: clamp(initialPollMs, MIN_POLL_MS, MAX_POLL_MS),
    });

    const schedule = (delayMs) => {
        if (stoppedRef.current) return;
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(tick, delayMs);
    };

    const effectiveHandlers = useMemo(() => handlers || {}, [handlers]);

    const tick = async () => {
        if (!enabled || stoppedRef.current) return;
        if (inFlightRef.current) return;

        inFlightRef.current = true;
        try {
            const res = await pollOnce();
            const events = Array.isArray(res?.events) ? res.events : [];
            const nextPollInMs = typeof res?.nextPollInMs === 'number' ? res.nextPollInMs : null;

            backoffRef.current.currentMs = backoffRef.current.baseMs;

            for (const evt of events) {
                const id = evt?.id;
                const type = evt?.type;
                if (!type) continue;

                if (id && seenEventIdsRef.current.has(id)) continue;
                if (id) seenEventIdsRef.current.add(id);

                const handler = effectiveHandlers[type];
                if (!handler) {
                    // eslint-disable-next-line no-console
                    console.info('Unhandled poll event type:', type, evt);
                    continue;
                }

                try {
                    await handler(evt?.payload, evt);
                } catch (handlerError) {
                    // eslint-disable-next-line no-console
                    console.warn('Poll handler failed:', type, handlerError);
                }
            }

            schedule(clamp(nextPollInMs ?? backoffRef.current.baseMs, MIN_POLL_MS, MAX_POLL_MS));
        } catch (error) {
            const next = clamp(backoffRef.current.currentMs * 2, MIN_POLL_MS, MAX_POLL_MS);
            backoffRef.current.currentMs = next;
            schedule(next);
        } finally {
            inFlightRef.current = false;
        }
    };

    useEffect(() => {
        stoppedRef.current = false;

        if (!enabled) return () => { };

        schedule(backoffRef.current.baseMs);

        return () => {
            stoppedRef.current = true;
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            inFlightRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, effectiveHandlers]);
};

